/* istanbul ignore file */
// Sentry SDK initialization wrapper — not unit-testable without mocking
// the entire SDK. Behavior verified by manual smoke test on real envs
// (events appear / don't appear in the Sentry dashboard per expected
// gating rules).
import * as Sentry from "@sentry/react";
import type { RuntimeConfig } from "../runtimeConfig.js";

const SENTRY_DSN =
  "https://04cdd66329cf4f138d7e6edbe37c5785@o1127144.ingest.sentry.io/6168786";

interface InitSentryOptions {
  runtimeConfig?: RuntimeConfig;
  initialScope?: Parameters<typeof Sentry.init>[0]["initialScope"];
}

/**
 * Initialise Sentry. Safe to call before runtime config is loaded — in that
 * case the SDK initialises with `environment: "bootstrap"` so config-load
 * errors can still be captured. After `loadRuntimeConfig()` succeeds, call
 * again with the config to attach the real environment / replay sample
 * rates etc.
 */
export function initSentry({
  runtimeConfig,
  initialScope,
}: InitSentryOptions = {}): void {
  const deployEnv = runtimeConfig?.VITE_DEPLOY_ENV;
  // Bootstrap-stage (deployEnv === undefined): enable only on production
  // builds (Vite-built bundles served from a real env), so config-fetch
  // failures still reach Sentry from stg/partner/prod but local Vite
  // `pnpm dev` runs (where PROD === false) stay silent. Post-config
  // (deployEnv set): defer to the explicit `local` opt-out.
  const enabled =
    deployEnv === undefined ? import.meta.env.PROD : deployEnv !== "local";
  // Add browser tracing + Replay only in the post-config init pass.
  // Session Replay is a singleton per page — initialising it twice (once
  // in the bootstrap-stage init, once in the post-config init) throws
  // "Multiple Sentry Session Replay instances are not supported" and
  // breaks bootstrap. Bootstrap-stage init still captures exceptions
  // (basic SDK functionality, no integrations needed for that).
  const isPostConfig = deployEnv !== undefined;
  const integrations = isPostConfig
    ? [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Mask all text and inputs so replays never capture PII/credentials.
          // Safe static chrome opts back in via the `sentry-unmask` class or a
          // structural selector.
          //
          // Rule: unmask text that comes from a translation key, never text
          // interpolated from data. A selector match on any ancestor unmasks
          // that element's whole subtree, so opt in per element — never on a
          // list or container whose items may render customer data. The
          // secondary nav is the cautionary case: its labels are the viewer's
          // organisation title and full name.
          maskAllText: true,
          maskAllInputs: true,
          // `.MuiButton-root` and `[role="tab"]` are structural selectors for
          // action chrome. Every button label in this app and in @msys/ui is
          // sourced from a translation key — audited across all 432 <Button>
          // elements with text children, including the prop-driven ones
          // (`label`, `title`, `buttonText`, `saveButtonLabel`, …), which all
          // default to or receive `t(...)` values. Re-audit before assuming
          // this still holds if buttons start rendering record titles.
          unmask: [".sentry-unmask", ".MuiButton-root", '[role="tab"]'],
          // `blockAllMedia` defaults to true in the SDK, which blocks every
          // icon and leaves replays hard to follow. Icons are static assets
          // and cannot carry customer data, so they opt back in. `img` and
          // `video` stay blocked — they render building photos, attachments,
          // PDF previews and avatars. Never widen this to all `svg`: planning
          // and absence views draw people's names as SVG text.
          unblock: [".MuiSvgIcon-root"],
        }),
      ]
    : [];
  // Release tag tracks the deployed commit SHA from runtime config
  // (`/config.json` `version`, populated by the CodePipeline deploy
  // stage). The bootstrap-stage init has no runtime config yet, so
  // events captured before `loadRuntimeConfig()` resolves carry no
  // release — accepted trade-off for catching config-load failures.
  Sentry.init({
    enabled,
    dsn: SENTRY_DSN,
    integrations,
    environment: deployEnv ?? "bootstrap",
    initialScope,
    release: runtimeConfig?.version,
    beforeSend: function (event, hint) {
      const exception = hint?.originalException;

      if (exception instanceof Error) {
        event.fingerprint = ["{{ default }}", exception.message];
      }

      return event;
    },
    replaysSessionSampleRate:
      deployEnv === "dev" || deployEnv === "stg" ? 1 : 0,
    replaysOnErrorSampleRate: 1.0,
  });
}
